import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, useMap, Marker, Popup } from 'react-leaflet';
import { Form, Card } from 'react-bootstrap';
import { Snowflake, Cable, Mountain, MapPin, ChevronLeft, ChevronRight, Box, Compass } from 'lucide-react';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import RegionalMap3D from './RegionalMap3D';
import { loadGlobalTouringRoutes } from './touring/touringRoutesData';
import { attachTouringRoutesLeaflet } from './touring/touringRoutesLayer';

const VIEW_3D_MIN_ZOOM = 8;

const leafletBoundsToLngLatBounds = (b) => {
  if (!b) return null;
  return [[b.getWest(), b.getSouth()], [b.getEast(), b.getNorth()]];
};

// Set up default icon for Leaflet
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Helper function to format snowfall amounts
const formatSnowfall = (cm) => {
  if (cm === null || cm === undefined || isNaN(cm)) return '0.0 cm';
  return `${Number(cm).toFixed(1)} cm`;
};

const toResortSlug = (name, id) => {
  const slug = name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug}-${id.substring(0, 8)}`;
};

const createPopupContent = (properties) => {
  // Handle both nested and flat data structures
  const snowfall24h = properties.recentSnowfall?.snowfall24h ?? properties.snowfall24h;
  const snowfall48h = properties.recentSnowfall?.snowfall48h ?? properties.snowfall48h;
  const snowfall72h = properties.recentSnowfall?.snowfall72h ?? properties.snowfall72h;
  
  return `
    <div class="card border-0" style="min-width: 220px; max-width: 320px; background: var(--pm-bg-surface); color: var(--pm-text); overflow: hidden; border-radius: 12px;">
      <div style="background: var(--pm-popup-header-bg); padding: 12px 16px; margin: -1px -1px 0 -1px;">
        <h5 class="mb-0" style="color: var(--pm-popup-header-text);">${properties.name || 'Unknown Location'}</h5>
      </div>
      <div class="card-body p-3" style="background: var(--pm-bg-surface);">
        <!-- Forecast Section -->
        <div class="mb-3">
          <h6 class="mb-2 small fw-bold" style="color: var(--pm-text-muted);">Snow Forecast</h6>
          <div class="row g-2">
            <div class="col-4">
              <div style="background: var(--pm-bg-elevated); border: 1px solid var(--pm-border); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">Next 24h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(properties.next24Hours)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: var(--pm-bg-elevated); border: 1px solid var(--pm-border); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">Next 48h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(properties.next48Hours)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: var(--pm-bg-elevated); border: 1px solid var(--pm-border); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">Next 72h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(properties.next72Hours)}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Snowfall Section -->
        <div>
          <h6 class="mb-2 small fw-bold" style="color: var(--pm-text-muted);">Recent Snowfall</h6>
          <div class="row g-2">
            <div class="col-4">
              <div style="background: rgba(0, 212, 255, 0.08); border: 1px solid var(--pm-accent-muted); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">24h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(snowfall24h)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: rgba(0, 212, 255, 0.08); border: 1px solid var(--pm-accent-muted); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">48h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(snowfall48h)}</div>
              </div>
            </div>
            <div class="col-4">
              <div style="background: rgba(0, 212, 255, 0.08); border: 1px solid var(--pm-accent-muted); border-radius: 6px; padding: 8px;">
                <div class="small" style="color: var(--pm-text-muted);">72h</div>
                <div class="fw-bold" style="font-family: var(--pm-font-data); color: var(--pm-text);">${formatSnowfall(snowfall72h)}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Resort Quick Info -->
        <div class="mt-3 pt-3" style="border-top: 1px solid var(--pm-border);">
          <div class="row text-center">
            ${properties.verticalDrop ? `
              <div class="col-4">
                <small style="color: var(--pm-text-muted);" class="d-block">Vertical</small>
                <strong class="small" style="color: var(--pm-text);">${properties.verticalDrop}m</strong>
              </div>
            ` : ''}
            ${properties.liftCount ? `
              <div class="col-4">
                <small style="color: var(--pm-text-muted);" class="d-block">Lifts</small>
                <strong class="small" style="color: var(--pm-text);">${properties.liftCount}</strong>
              </div>
            ` : ''}
            ${properties.maxElevation ? `
              <div class="col-4">
                <small style="color: var(--pm-text-muted);" class="d-block">Summit</small>
                <strong class="small" style="color: var(--pm-text);">${properties.maxElevation}m</strong>
              </div>
            ` : ''}
          </div>
        </div>

        <!-- View Details Button -->
        <div class="mt-3 pt-3" style="border-top: 1px solid var(--pm-border);">
          <button
            class="btn btn-primary btn-sm w-100"
            onclick="window.location.href='/resort/${toResortSlug(properties.name, properties.id)}'"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-2">
              <path d="m9 18 6-6-6-6"/>
            </svg>
            View Full Details
          </button>
        </div>
      </div>
    </div>
  `;
};

const SnowFilter = ({
  onFilterChange,
  timeframe,
  onTimeframeChange,
  recentTimeframe,
  onRecentTimeframeChange,
  isRecentSnowfall,
  onRecentToggle,
  filters,
  onFiltersChange,
  allResorts,
  expandedFilter,
  onExpandedFilterClear,
  onRegionSelect,
  touringEnabled,
  onTouringToggle,
  mapZoom,
}) => {
  const snowRef = React.useRef(null);
  const liftsRef = React.useRef(null);
  const elevationRef = React.useRef(null);
  const regionRef = React.useRef(null);
  const touringRef = React.useRef(null);

  useEffect(() => {
    if (!expandedFilter) return;
    const refMap = { snow: snowRef, lifts: liftsRef, elevation: elevationRef, region: regionRef, touring: touringRef };
    const ref = refMap[expandedFilter];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onExpandedFilterClear();
    }
  }, [expandedFilter, onExpandedFilterClear]);
  const [forecastMin, setForecastMin] = useState(0);
  const [recentMin, setRecentMin] = useState(0);
  const [liftRange, setLiftRange] = useState([0, 50]);
  const [elevationRange, setElevationRange] = useState([0, 5000]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [regionSearchTerm, setRegionSearchTerm] = useState('');

  // Get unique regions from data
  const regions = [...new Set(allResorts?.features?.map(f => f.properties.region).filter(Boolean) || [])].sort();
  
  // Filter regions based on search term
  const filteredRegions = regions.filter(region => 
    region.toLowerCase().includes(regionSearchTerm.toLowerCase())
  );

  const handleSnowSliderChange = (value) => {
    if (isRecentSnowfall) {
      setRecentMin(value);
      onFilterChange(value);
    } else {
      setForecastMin(value);
      onFilterChange(value);
    }
  };

  const handleRegionChange = (region) => {
    const isAdding = !selectedRegions.includes(region);
    const newSelection = isAdding
      ? [...selectedRegions, region]
      : selectedRegions.filter(r => r !== region);
    setSelectedRegions(newSelection);
    onFiltersChange({ ...filters, regions: newSelection });
    if (isAdding && onRegionSelect) {
      onRegionSelect(region);
    }
  };

  const handleLiftRangeChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    setLiftRange([val, liftRange[1]]);
    onFiltersChange({ ...filters, lifts: { min: val, max: Infinity } });
  };

  const handleElevationRangeChange = (e) => {
    const newRange = [parseInt(e.target.value) || 0, elevationRange[1]];
    setElevationRange(newRange);
    onFiltersChange({ ...filters, elevation: { min: newRange[0], max: newRange[1] } });
  };

  const toggleSnowfallType = (recent) => {
    onRecentToggle(recent);
    onFilterChange(recent ? recentMin : forecastMin);
  };

  return (
    <div className="p-3" style={{ paddingBottom: '40px' }}>
      <div className="d-flex flex-column gap-3">
        
        {/* Snowfall Forecast/Recent Card */}
        <Card
          ref={snowRef}
          className="border-0 shadow-sm"
          style={{
            borderRadius: '12px',
            background: 'var(--pm-bg-surface)'
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Snowflake size={18} style={{ color: 'var(--pm-accent)' }} />
              <h6 className="mb-0 fw-bold">Snowfall</h6>
            </div>
            
            {/* Toggle between Forecast and Recent */}
            <div className="btn-group w-100 mb-3" role="group">
              <button
                type="button"
                className={`btn ${!isRecentSnowfall ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => toggleSnowfallType(false)}
                style={{ fontSize: '14px' }}
              >
                Forecast
              </button>
              <button
                type="button"
                className={`btn ${isRecentSnowfall ? 'btn-info' : 'btn-outline-info'}`}
                onClick={() => toggleSnowfallType(true)}
                style={{ fontSize: '14px' }}
              >
                Recent
              </button>
            </div>

            {/* Timeframe Selector */}
            <div className="mb-3">
              {!isRecentSnowfall ? (
                <Form.Select
                  value={timeframe}
                  onChange={(e) => onTimeframeChange(e.target.value)}
                  size="sm"
                  style={{ fontSize: '14px' }}
                >
                  <option value="next24Hours">Next 24 Hours</option>
                  <option value="next48Hours">Next 48 Hours</option>
                  <option value="next72Hours">Next 72 Hours</option>
                </Form.Select>
              ) : (
                <Form.Select
                  value={recentTimeframe}
                  onChange={(e) => onRecentTimeframeChange(e.target.value)}
                  size="sm"
                  style={{ fontSize: '14px' }}
                >
                  <option value="snowfall24h">Last 24 Hours</option>
                  <option value="snowfall48h">Last 48 Hours</option>
                  <option value="snowfall72h">Last 72 Hours</option>
                </Form.Select>
              )}
            </div>

            {/* Snowfall Minimum Slider */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small" style={{ color: 'var(--pm-text-muted)' }}>Minimum snowfall</span>
                <span className="fw-bold" style={{ fontSize: '14px', color: 'var(--pm-accent)' }}>
                  {(isRecentSnowfall ? recentMin : forecastMin) >= 50 ? '50+' : (isRecentSnowfall ? recentMin : forecastMin)} cm
                </span>
              </div>
              <input
                type="range"
                className="form-range"
                min="0"
                max="50"
                step="1"
                value={isRecentSnowfall ? recentMin : forecastMin}
                onChange={(e) => handleSnowSliderChange(parseInt(e.target.value))}
                style={{ accentColor: 'var(--pm-accent)' }}
              />
              <div className="d-flex justify-content-between">
                <span className="small" style={{ color: 'var(--pm-text-muted)' }}>0 cm</span>
                <span className="small" style={{ color: 'var(--pm-text-muted)' }}>50+ cm</span>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Number of Lifts Card */}
        <Card
          ref={liftsRef}
          className="border-0 shadow-sm"
          style={{
            borderRadius: '12px',
            background: 'var(--pm-bg-surface)'
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Cable size={18} style={{ color: 'var(--pm-accent)' }} />
              <h6 className="mb-0 fw-bold">Number of Lifts</h6>
            </div>
            
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small" style={{ color: 'var(--pm-text-muted)' }}>Minimum lifts</span>
                <span className="fw-bold" style={{ fontSize: '14px', color: 'var(--pm-accent)' }}>
                  {liftRange[0] >= 70 ? '70+' : liftRange[0]}
                </span>
              </div>

              <input
                type="range"
                className="form-range"
                min="0"
                max="70"
                value={liftRange[0]}
                onChange={handleLiftRangeChange}
                style={{ accentColor: 'var(--pm-accent)' }}
              />
              <div className="d-flex justify-content-between">
                <span className="small" style={{ color: 'var(--pm-text-muted)' }}>0</span>
                <span className="small" style={{ color: 'var(--pm-text-muted)' }}>70+</span>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Summit Elevation Card */}
        <Card
          ref={elevationRef}
          className="border-0 shadow-sm"
          style={{
            borderRadius: '12px',
            background: 'var(--pm-bg-surface)'
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Mountain size={18} style={{ color: 'var(--pm-accent)' }} />
              <h6 className="mb-0 fw-bold">Summit Elevation</h6>
            </div>
            
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">Min: {elevationRange[0]}m</span>
                <span className="small text-muted">Max: {elevationRange[1]}m</span>
              </div>
              
              <input
                type="range"
                className="form-range"
                min="0"
                max="5000"
                step="100"
                value={elevationRange[0]}
                onChange={handleElevationRangeChange}
                style={{ accentColor: 'var(--pm-accent)' }}
              />
            </div>
          </Card.Body>
        </Card>

        {/* Region Filter Card */}
        <Card
          ref={regionRef}
          className="border-0 shadow-sm"
          style={{
            borderRadius: '12px',
            background: 'var(--pm-bg-surface)'
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <MapPin size={18} style={{ color: 'var(--pm-accent)' }} />
              <h6 className="mb-0 fw-bold">Region</h6>
              {selectedRegions.length > 0 && (
                <button
                  className="btn btn-sm ms-auto p-0 px-1"
                  style={{ fontSize: '0.7rem', color: 'var(--pm-text-muted)', border: '1px solid var(--pm-border)', borderRadius: '4px', lineHeight: 1.6 }}
                  onClick={() => { setSelectedRegions([]); onFiltersChange({ ...filters, regions: [] }); }}
                >
                  Reset
                </button>
              )}
            </div>
            
            {/* Search Input */}
            <Form.Control
              type="text"
              placeholder="Search regions..."
              value={regionSearchTerm}
              onChange={(e) => setRegionSearchTerm(e.target.value)}
              size="sm"
              className="mb-2"
              style={{ fontSize: '14px' }}
            />
            
            {/* Region Checkboxes with scroll */}
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filteredRegions.length > 0 ? (
                filteredRegions.map((region) => (
                  <Form.Check
                    key={region}
                    type="checkbox"
                    id={`region-${region}`}
                    label={region}
                    checked={selectedRegions.includes(region)}
                    onChange={() => handleRegionChange(region)}
                    className="mb-2"
                    style={{ fontSize: '14px' }}
                  />
                ))
              ) : (
                <div className="text-muted small text-center py-2">
                  No regions found
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Touring Routes Card */}
        <Card
          ref={touringRef}
          className="border-0 shadow-sm"
          style={{
            borderRadius: '12px',
            background: 'var(--pm-bg-surface)'
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Compass size={18} style={{ color: '#a855f7' }} />
              <h6 className="mb-0 fw-bold">Touring routes</h6>
              <Form.Check
                type="switch"
                id="touring-toggle"
                className="ms-auto"
                checked={!!touringEnabled}
                onChange={(e) => onTouringToggle(e.target.checked)}
              />
            </div>
            <div className="small" style={{ color: 'var(--pm-text-muted)', lineHeight: 1.4 }}>
              Off-piste / ski-touring routes from OpenStreetMap and Skitourenguru (Switzerland).
              {touringEnabled && mapZoom != null && mapZoom < 10 && (
                <div className="mt-2 small" style={{ color: '#a855f7' }}>
                  Zoom in to z≥10 to see routes.
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

      </div>
    </div>
  );
};

const WebGLOverlay = ({ data }) => {
  const map = useMap();
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    if (!data || !data.features || data.features.length === 0) {
      console.error('No valid data for WebGLOverlay');
      return;
    }

    try {
      const CanvasLayer = L.Layer.extend({
        onAdd: function(map) {
          this._map = map;
          this._canvas = L.DomUtil.create('canvas', 'leaflet-layer');
          this._ctx = this._canvas.getContext('webgl') || this._canvas.getContext('experimental-webgl');

          if (!this._ctx) {
            console.error('WebGL not supported, falling back to regular markers');
            setFallback(true);
            return;
          }

          const gl = this._ctx;
          const size = this._map.getSize();
          this._canvas.width = size.x;
          this._canvas.height = size.y;

          // Cache compiled shader program and attribute/uniform locations
          this._program = this._getProgram(gl);
          this._positionLoc = gl.getAttribLocation(this._program, "a_position");
          this._colorLoc = gl.getAttribLocation(this._program, "a_color");
          this._resolutionLoc = gl.getUniformLocation(this._program, "u_resolution");

          // Create reusable buffers
          this._positionBuffer = gl.createBuffer();
          this._colorBuffer = gl.createBuffer();

          // Pre-populate color buffer once (all points are blue)
          const colorData = new Float32Array(data.features.length * 3);
          for (let i = 0; i < data.features.length; i++) {
            colorData[i * 3 + 2] = 1; // blue channel
          }
          gl.bindBuffer(gl.ARRAY_BUFFER, this._colorBuffer);
          gl.bufferData(gl.ARRAY_BUFFER, colorData, gl.STATIC_DRAW);

          this._visibleFeatures = [];
          this._debounceTimer = null;
          this._isHidden = false;

          this._canvas.style.transition = 'opacity 0.15s ease-in';

          map.getPanes().overlayPane.appendChild(this._canvas);
          map.on('moveend', this._reset, this);
          map.on('movestart', this._onMoveStart, this);
          map.on('click', this._onClick, this);

          // Direct initial render (avoids 200ms debounce delay on load)
          const topLeft = this._map.containerPointToLayerPoint([0, 0]);
          L.DomUtil.setPosition(this._canvas, topLeft);
          this._redraw();
        },

        onRemove: function(map) {
          if (this._ctx) {
            const gl = this._ctx;
            if (this._positionBuffer) gl.deleteBuffer(this._positionBuffer);
            if (this._colorBuffer) gl.deleteBuffer(this._colorBuffer);
            if (this._program) gl.deleteProgram(this._program);
          }
          if (this._canvas) {
            map.getPanes().overlayPane.removeChild(this._canvas);
          }
          map.off('moveend', this._reset, this);
          map.off('movestart', this._onMoveStart, this);
          map.off('click', this._onClick, this);
          if (this._debounceTimer) clearTimeout(this._debounceTimer);
        },

        _reset: function() {
          const topLeft = this._map.containerPointToLayerPoint([0, 0]);
          L.DomUtil.setPosition(this._canvas, topLeft);
          if (this._debounceTimer) clearTimeout(this._debounceTimer);
          this._debounceTimer = setTimeout(() => {
            this._debounceTimer = null;
            this._redraw();
            this._showCanvas();
          }, 200);
        },

        _onMoveStart: function() {
          if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
          }
          this._hideCanvas();
        },

        _hideCanvas: function() {
          if (this._isHidden) return;
          this._isHidden = true;
          this._canvas.style.transition = 'none';
          this._canvas.style.opacity = '0';
        },

        _showCanvas: function() {
          this._isHidden = false;
          this._canvas.style.transition = 'opacity 0.15s ease-in';
          this._canvas.style.opacity = '1';
        },

        _redraw: function() {
          if (!this._map || !this._ctx) return;

          const gl = this._ctx;
          const size = this._map.getSize();
          this._canvas.width = size.x;
          this._canvas.height = size.y;

          gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
          gl.clear(gl.COLOR_BUFFER_BIT);

          gl.useProgram(this._program);
          gl.uniform2f(this._resolutionLoc, gl.canvas.width, gl.canvas.height);

          // Viewport culling — skip offscreen points
          const bounds = this._map.getBounds();
          const pad = 0.5;
          const south = bounds.getSouth() - pad;
          const north = bounds.getNorth() + pad;
          const west = bounds.getWest() - pad;
          const east = bounds.getEast() + pad;

          this._visibleFeatures = [];
          this._positions = [];
          for (let i = 0; i < data.features.length; i++) {
            const feature = data.features[i];
            const [lon, lat] = feature.geometry.coordinates;
            if (lat < south || lat > north || lon < west || lon > east) continue;
            const point = this._map.latLngToContainerPoint([lat, lon]);
            this._positions.push(point.x, point.y);
            this._visibleFeatures.push(feature);
          }

          const pointCount = this._positions.length / 2;
          if (pointCount === 0) return;

          // Upload position data (reuse buffer, DYNAMIC_DRAW)
          gl.bindBuffer(gl.ARRAY_BUFFER, this._positionBuffer);
          gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this._positions), gl.DYNAMIC_DRAW);
          gl.enableVertexAttribArray(this._positionLoc);
          gl.vertexAttribPointer(this._positionLoc, 2, gl.FLOAT, false, 0, 0);

          // Bind pre-built color buffer (no re-upload needed)
          gl.bindBuffer(gl.ARRAY_BUFFER, this._colorBuffer);
          gl.enableVertexAttribArray(this._colorLoc);
          gl.vertexAttribPointer(this._colorLoc, 3, gl.FLOAT, false, 0, 0);

          gl.drawArrays(gl.POINTS, 0, pointCount);
        },

        _onClick: function(e) {
          const clickPoint = [e.containerPoint.x, e.containerPoint.y];
          const tolerance = 5; // pixels

          for (let i = 0; i < this._positions.length; i += 2) {
            const dx = this._positions[i] - clickPoint[0];
            const dy = this._positions[i + 1] - clickPoint[1];
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= tolerance) {
              const feature = this._visibleFeatures[i / 2];
              const popupContent = createPopupContent(feature.properties);
              L.popup({ autoPanPadding: [40, 40], maxWidth: 320 })
                .setLatLng(this._map.containerPointToLatLng([this._positions[i], this._positions[i + 1]]))
                .setContent(popupContent)
                .openOn(this._map);
              break;
            }
          }
        },

        _getProgram: function(gl) {
          const vertexShader = `
            attribute vec2 a_position;
            attribute vec3 a_color;
            uniform vec2 u_resolution;
            varying vec3 v_color;
            void main() {
              vec2 zeroToOne = a_position / u_resolution;
              vec2 zeroToTwo = zeroToOne * 2.0;
              vec2 clipSpace = zeroToTwo - 1.0;
              gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
              gl_PointSize = 5.0;
              v_color = a_color;
            }
          `;

          const fragmentShader = `
            precision mediump float;
            varying vec3 v_color;
            void main() {
              gl_FragColor = vec4(v_color, 1);
            }
          `;

          const program = gl.createProgram();
          const vs = gl.createShader(gl.VERTEX_SHADER);
          const fs = gl.createShader(gl.FRAGMENT_SHADER);

          gl.shaderSource(vs, vertexShader);
          gl.compileShader(vs);
          gl.shaderSource(fs, fragmentShader);
          gl.compileShader(fs);
          gl.attachShader(program, vs);
          gl.attachShader(program, fs);
          gl.linkProgram(program);

          return program;
        }
      });

      const canvasLayer = new CanvasLayer();
      map.addLayer(canvasLayer);

      return () => {
        map.removeLayer(canvasLayer);
      };
    } catch (error) {
      console.error('Error in WebGL rendering:', error);
      setFallback(true);
    }
  }, [map, data]);

  if (fallback) {
    return data.features.map((feature, index) => (
      <Marker key={index} position={[feature.geometry.coordinates[1], feature.geometry.coordinates[0]]}>
        <Popup>{createPopupContent(feature.properties)}</Popup>
      </Marker>
    ));
  }

  return null;
};

// Off-piste / ski-touring routes overlay. Lazy-loads
// /data/touring-routes.geojson on first activation; the controller handles
// zoom-gating (only renders at zoom ≥ 10) so panning at world scale stays fast.
const TouringRoutesOverlay = ({ enabled }) => {
  const map = useMap();
  const ctrlRef = useRef(null);
  const dataRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      if (ctrlRef.current) ctrlRef.current.setVisible(false);
      return undefined;
    }
    (async () => {
      if (!dataRef.current) {
        dataRef.current = await loadGlobalTouringRoutes();
      }
      if (cancelled) return;
      if (!ctrlRef.current) {
        ctrlRef.current = attachTouringRoutesLeaflet(map, L, dataRef.current, {
          visible: true,
          minZoom: 10,
        });
      } else {
        ctrlRef.current.setVisible(true);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, map]);

  useEffect(() => () => {
    if (ctrlRef.current) {
      ctrlRef.current.destroy();
      ctrlRef.current = null;
    }
  }, []);

  return null;
};

const MapRefSetter = ({ mapRef, onViewChange }) => {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    if (!onViewChange) return undefined;
    const emit = () => onViewChange({ zoom: map.getZoom(), bounds: map.getBounds() });
    emit();
    map.on('zoomend', emit);
    map.on('moveend', emit);
    return () => {
      map.off('zoomend', emit);
      map.off('moveend', emit);
    };
  }, [map, mapRef, onViewChange]);
  return null;
};

const FlyToHandler = () => {
  const map = useMap();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.flyTo) {
      const { lat, lng, properties } = location.state.flyTo;
      map.flyTo([lat, lng], 12, { duration: 1.5 });
      map.once('moveend', () => {
        if (properties) {
          L.popup({ autoPanPadding: [40, 40], maxWidth: 320 })
            .setLatLng([lat, lng])
            .setContent(createPopupContent(properties))
            .openOn(map);
        }
      });
      navigate('/', { replace: true, state: {} });
    }
  }, [location.state, map, navigate]);

  return null;
};

const SkiAreasMap = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('next24Hours');
  const [recentTimeframe, setRecentTimeframe] = useState('snowfall24h');
  const [filters, setFilters] = useState({
    countries: [],
    regions: [],
    lifts: { min: 0, max: Infinity },
    elevation: { min: 0, max: 5000 }
  });
  const [snowMin, setSnowMin] = useState(0);
  const [isRecentSnowfall, setIsRecentSnowfall] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth <= 768);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [viewMode, setViewMode] = useState('2d');
  const [mapZoom, setMapZoom] = useState(4);
  const [touringEnabled, setTouringEnabled] = useState(false);
  const mapBoundsRef = useRef(null);
  const mapRef = useRef(null);

  const handleMapViewChange = useCallback(({ zoom, bounds }) => {
    setMapZoom(zoom);
    mapBoundsRef.current = bounds;
  }, []);

  const flyToRegion = useCallback((region) => {
    if (!mapRef.current || !weatherData) return;
    const regionFeatures = weatherData.features.filter(
      f => f.properties.region === region
    );
    if (regionFeatures.length === 0) return;
    const lats = regionFeatures.map(f => f.geometry.coordinates[1]);
    const lngs = regionFeatures.map(f => f.geometry.coordinates[0]);
    const bounds = L.latLngBounds(
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    );
    mapRef.current.flyToBounds(bounds, { padding: [50, 50], duration: 1.5, maxZoom: 10 });
  }, [weatherData]);

  useEffect(() => {
    console.log('Fetching weather data...');
    fetch('/api/ski_areas')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('Raw GeoJSON data received:', data);
        // Log a sample feature to check its structure
        if (data.features && data.features.length > 0) {
          console.log('Sample feature properties:', data.features[0].properties);
        }
        if (data.error) {
          throw new Error(data.error);
        }
        setWeatherData(data);
        setFilteredData(data);
      })
      .catch(error => {
        console.error('Error fetching weather data:', error);
        setError(error.message);
      });
  }, []);

  const applyAllFilters = (minSnow = snowMin, currentFilters = filters, recentSnow = isRecentSnowfall) => {
    if (!weatherData) return;

    const filtered = {
      ...weatherData,
      features: weatherData.features.filter(feature => {
        const props = feature.properties;

        // Snow amount filter (forecast or recent)
        let snowAmount;
        if (recentSnow) {
          snowAmount = props.recentSnowfall?.[recentTimeframe] ?? props[recentTimeframe] ?? 0;
        } else {
          snowAmount = props[timeframe] ?? 0;
        }

        if (snowAmount < minSnow) return false;

        // Country filter
        if (currentFilters.countries.length > 0) {
          if (!currentFilters.countries.includes(props.country)) return false;
        }

        // Region filter
        if (currentFilters.regions.length > 0) {
          if (!currentFilters.regions.includes(props.region)) return false;
        }

        // Lift count filter
        const liftCount = props.liftCount || 0;
        if (liftCount < currentFilters.lifts.min || liftCount > currentFilters.lifts.max) {
          return false;
        }

        // Elevation filter
        const elevation = props.maxElevation || 0;
        if (elevation < currentFilters.elevation.min || elevation > currentFilters.elevation.max) {
          return false;
        }

        return true;
      })
    };

    setFilteredData(filtered);
  };

  const handleFilterChange = (minSnow) => {
    setSnowMin(minSnow);
    applyAllFilters(minSnow, filters, isRecentSnowfall);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    applyAllFilters(snowMin, newFilters, isRecentSnowfall);
  };

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    setTimeout(() => applyAllFilters(snowMin, filters, isRecentSnowfall), 0);
  };

  const handleRecentTimeframeChange = (newTimeframe) => {
    setRecentTimeframe(newTimeframe);
    setTimeout(() => applyAllFilters(snowMin, filters, isRecentSnowfall), 0);
  };

  useEffect(() => {
    // Re-apply filters when timeframe changes
    if (weatherData) {
      applyAllFilters(snowMin, filters, isRecentSnowfall);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, recentTimeframe, weatherData, snowMin, filters, isRecentSnowfall]);

  if (error) {
    return (
      <div 
        className="d-flex align-items-center justify-content-center"
        style={{ 
          height: '100vh', 
          width: '100%',
          background: 'var(--pm-bg-page)',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999
        }}
      >
        <div className="text-center">
          <div className="alert alert-danger">
            <h5>Error loading weather data</h5>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!weatherData) {
    return (
      <div 
        className="d-flex align-items-center justify-content-center"
        style={{ 
          height: '100vh', 
          width: '100%',
          background: 'var(--pm-bg-page)',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999
        }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading ski resort data...</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="map-wrapper" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {viewMode === '2d' ? (
        <>
          <MapContainer
            center={[40, -98]}
            zoom={4}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            zoomControl={false}
            scrollWheelZoom={true}
            zoomSnap={1}
            zoomDelta={1}
            wheelDebounceTime={150}
            wheelPxPerZoomLevel={120}
            maxBounds={[[-89.98155760646617, -180], [89.99346179538875, 180]]}
            maxBoundsViscosity={1.0}
            worldCopyJump={false}
            minZoom={2}
          >
            <TileLayer
              url="https://api.mapbox.com/styles/v1/alexandergoddard/cm31yrcom002n01qqbdpzb04o/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYWxleGFuZGVyZ29kZGFyZCIsImEiOiJjbHM3cDk4NTgyMWc2MmxwYzY5ZGJocjZqIn0.Ac6J909xbTeG09-m7e5Oyw"
              attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>'
              tileSize={512}
              zoomOffset={-1}
              maxZoom={19}
            />
            {filteredData && <WebGLOverlay data={filteredData} />}
            <TouringRoutesOverlay enabled={touringEnabled} />
            <FlyToHandler />
            <MapRefSetter mapRef={mapRef} onViewChange={handleMapViewChange} />
          </MapContainer>
          {mapZoom >= VIEW_3D_MIN_ZOOM && (
            <button
              type="button"
              className="skiareas-3d-toggle"
              onClick={() => setViewMode('3d')}
              title="Switch to 3D terrain view"
            >
              <Box size={14} />
              <span>3D view</span>
            </button>
          )}
        </>
      ) : (
        <RegionalMap3D
          resorts={filteredData}
          initialBounds={leafletBoundsToLngLatBounds(mapBoundsRef.current)}
          onClose={() => setViewMode('2d')}
        />
      )}

      {/* Mobile backdrop */}
      {!sidebarCollapsed && (
        <div
          className="sidebar-backdrop"
          onClick={() => { setSidebarCollapsed(true); setExpandedFilter(null); }}
        />
      )}

      {/* Mobile filter tab (collapsed state) */}
      {sidebarCollapsed && (
        <button
          className="mobile-filter-tab"
          onClick={() => setSidebarCollapsed(false)}
          aria-label="Open filters"
        >
          <Snowflake size={16} />
          <span>Filters</span>
        </button>
      )}

      {/* Sidebar */}
      <div className={`filter-sidebar ${sidebarCollapsed ? 'filter-sidebar--collapsed' : ''}`}>
        {sidebarCollapsed ? (
          <div className="d-flex flex-column align-items-center gap-2 pt-3">
            <button
              className="sidebar-icon-btn"
              onClick={() => setSidebarCollapsed(false)}
              title="Expand filters"
            >
              <ChevronRight size={18} />
            </button>
            <div className="sidebar-icon-divider" />
            {[
              { icon: Snowflake, label: 'Snowfall', key: 'snow' },
              { icon: Cable, label: 'Lifts', key: 'lifts' },
              { icon: Mountain, label: 'Elevation', key: 'elevation' },
              { icon: MapPin, label: 'Region', key: 'region' },
              { icon: Compass, label: 'Touring routes', key: 'touring' },
            ].map(({ icon: Icon, label, key }) => (
              <button
                key={key}
                className="sidebar-icon-btn"
                title={label}
                onClick={() => { setSidebarCollapsed(false); setExpandedFilter(key); }}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-end p-2 pb-0">
              <button
                className="sidebar-icon-btn"
                onClick={() => { setSidebarCollapsed(true); setExpandedFilter(null); }}
                title="Collapse filters"
              >
                <ChevronLeft size={18} />
              </button>
            </div>
            <SnowFilter
              onFilterChange={handleFilterChange}
              timeframe={timeframe}
              onTimeframeChange={handleTimeframeChange}
              recentTimeframe={recentTimeframe}
              onRecentTimeframeChange={handleRecentTimeframeChange}
              isRecentSnowfall={isRecentSnowfall}
              onRecentToggle={setIsRecentSnowfall}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              allResorts={weatherData}
              expandedFilter={expandedFilter}
              onExpandedFilterClear={() => setExpandedFilter(null)}
              onRegionSelect={flyToRegion}
              touringEnabled={touringEnabled}
              onTouringToggle={setTouringEnabled}
              mapZoom={mapZoom}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SkiAreasMap;